import { Component, OnInit, Input, ElementRef, ViewChild, OnChanges, OnDestroy, SimpleChanges, HostBinding, Optional, Self, Renderer2, Output } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChip, MatChipInputEvent, MatChipList } from '@angular/material/chips';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatAutocompleteSelectedEvent, MatAutocomplete } from '@angular/material/autocomplete';
import { ChooserBarItem } from './chooser-bar-item';
import { FormControl, NgControl, FormBuilder, FormGroup, ControlValueAccessor } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Moment } from 'moment';
import { getTenorChooserBarItems } from '../tenor-util';

@Component({
    selector: 'app-chooser-bar',
    templateUrl: './chooser-bar.component.html',
    styleUrls: ['./chooser-bar.component.scss'],
    providers: [{
        provide: MatFormFieldControl,
        useExisting: ChooserBarComponent
    }],
})
export class ChooserBarComponent implements OnInit, OnChanges, OnDestroy, MatFormFieldControl<string[]>, ControlValueAccessor {
    static nextId = 0;
    @ViewChild('chipList', { static: false }) chipList: MatChipList;
    @ViewChild('itemInput', { static: false }) itemInput: ElementRef<HTMLInputElement>;
    @ViewChild('auto', { static: false }) matAutocomplete: MatAutocomplete;

    itemCtrl = new FormControl();
    separatorKeysCodes: number[] = [ENTER, COMMA];

    @Input()
    items: ChooserBarItem[];
    @Input()
    chooserBarType: 'SELECT' | 'DATE' | 'TENOR' | 'LIST' = 'SELECT';
    @Input()
    ordered: boolean = true;
    @Input()
    inputType: string = 'text';

    // @Input()
    // isDateChooser: boolean;
    selectedItems: ChooserBarItem[] = [];
    selectedItemValues: string[] = [];
    filteredItems: Observable<ChooserBarItem[]>;
    stateChanges = new Subject<void>();

    @Input()
    maxAllowed = -1;

    @HostBinding() id = `chooser-bar-${ChooserBarComponent.nextId++}`;
    private _placeholder: string;
    focused = false;
    private _required = false;
    private _disabled = false;
    errorState = false;
    controlType = 'chooser-bar';
    @HostBinding('attr.aria-describedby') describedBy = '';

    // Material Form Field Resources (start) -------------------
    writeValue(obj: any): void {
        // this.renderer.setProperty(this.elRef.nativeElement, 'value', obj);

        if (!obj) {
            obj = [];
        }
        // this.parts.value.selectedItems = obj;
        // this.selectedItems = obj;
        this.value = obj;
    }
    @Output()
    onChange: any = (c: any) => { }
    
    onTouch: any = () => { }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
    }

    get value(): string[] | null {
        return this.selectedItems.map((m) => m.value);
    }
    set value(itemsToSet: string[] | null) {
        // this.parts.setValue({ selectedItems: itemsToSet || [] });
        if ((this.chooserBarType === 'DATE' || this.chooserBarType === 'LIST') && (!this.items || this.items.length === 0)) {
            // In this case we dont expect to initialize with "allowed set of dates.."
            // Instead it will always be blank so lefts auto-self initialize
            this.items = itemsToSet
                .map((item) => {
                    return {
                        label: item,
                        value: item,
                    } as ChooserBarItem;
                });
        }
        if (this.chooserBarType === 'TENOR' && (!this.items || this.items.length === 0)) {
            this.items = getTenorChooserBarItems();
        }
        if (!this.items || this.items.length === 0) {
            // Hasn't initialized yet
            this.selectedItemValues = itemsToSet;
        } else {
            this.selectedItemValues = undefined;
            if (itemsToSet && this.items) {
                this.selectedItems = this.items
                    .filter((c) => {
                        return itemsToSet.includes(c.value);
                    })
                    .sort((i1, i2) => {
                        return itemsToSet.indexOf(i1.value) < itemsToSet.indexOf(i2.value) ? -1 : 1;
                    })
                    ;
            } else {
                this.selectedItems = [];
            }
        }
        this.onChange(itemsToSet);
        this.stateChanges.next();
    }


    @Input()
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next();
    }


    constructor(
        fb: FormBuilder,
        private fm: FocusMonitor,
        private elRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        @Optional() @Self() public ngControl: NgControl,) {
        // this.parts = fb.group({
        //     'selectedItems': fb.array([])
        // });
        fm.monitor(elRef.nativeElement, true).subscribe({
            next: origin => {
                this.focused = !!origin;
                this.stateChanges.next();
            }
        });
        if (this.ngControl != null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.fm.stopMonitoring(this.elRef.nativeElement);
    }
    get empty() {
        const e = !this.selectedItems || this.selectedItems.length === 0;
        // console.log(`==Empty(${this.id}): ${e}`);
        return e;
    }
    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }
    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean { return this._disabled; }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.itemCtrl.disable() : this.itemCtrl.enable();
        this.stateChanges.next();
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }
    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.elRef.nativeElement.querySelector('input').focus();
        }
    }

    moveChip(direction: 'FIRST' | 'PREV' | 'NEXT' | 'LAST') {
        const selected: MatChip = this.chipList.selected as MatChip;
        if (selected) {
            this.chipList.chips
                .forEach((chip, index) => {
                    if (chip.selected) {
                        let newIndex;
                        if (direction === 'FIRST') {
                            newIndex = 0;
                        } else if (direction === 'PREV') {
                            newIndex = Math.max(0, index - 1);
                        } else if (direction === 'NEXT') {
                            newIndex = Math.min(index + 1, this.selectedItems.length - 1);
                        } else if (direction === 'LAST') {
                            newIndex = this.selectedItems.length - 1;
                        }
                        moveItemInArray(this.selectedItems, index, newIndex);
                        this.stateChanges.next();
                    }
                })
        }
    }

    moveFirst() {
        this.moveChip('FIRST');
    }
    movePrevious() {
        this.moveChip('PREV');
    }
    moveNext() {
        this.moveChip('NEXT');
    }
    moveLast() {
        this.moveChip('LAST');
    }

    // Material Form Field Resources (end) -------------------
    ngOnInit(): void {

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.items) {
            this.filteredItems = this.itemCtrl.valueChanges.pipe(
                startWith(null),
                map((value: ChooserBarItem | string | null) => {
                    let valueToFilter;
                    if (typeof value === 'string') {
                        valueToFilter = value;
                    } else if (value) {
                        valueToFilter = value.label;
                    }
                    return valueToFilter ? this.filterByValue(valueToFilter) : this.filterByUnselected();
                }
                ));
            if (this.selectedItemValues) {
                this.value = this.selectedItemValues;
                this.selectedItemValues = undefined;
                this.stateChanges.next();
            }
        }
    }

    removeItem(item: ChooserBarItem) {
        const index = this.selectedItems.indexOf(item);
        if (index >= 0) {
            this.selectedItems.splice(index, 1);
            this.selectedItems = this.selectedItems.slice();
        }
        this.onChange(this.value);
        this.stateChanges.next();
    }
    onAdd(event: MatChipInputEvent): void {
        // Add item only when MatAutocomplete is not open
        // To make sure this does not conflict with OptionSelected Event
        if (!this.matAutocomplete.isOpen) {
            const input = event.input;
            const value = event.value;

            // Add our item
            if ((value || '').trim()) {
                let foundItem;
                if (this.chooserBarType === 'LIST') {
                    foundItem = { label: value, value: value } as ChooserBarItem;
                } else {
                    foundItem = this.findItem(value.trim());
                }
                if (foundItem) {
                    this.selectedItems.push(foundItem);
                    this.onChange(foundItem);
                }
            }

            // Reset the input value
            if (input) {
                input.value = '';
            }

            this.itemCtrl.setValue(null);
        }
        this.onChange(this.value);
        this.stateChanges.next();
    }

    findItem(itemLabel: string): ChooserBarItem {
        return this.items
            .find((i) => {
                return i.label === itemLabel;
            });
    }

    onSelected(event: MatAutocompleteSelectedEvent | MatDatepickerInputEvent<Moment>): void {
        if (this.chooserBarType === 'DATE') {
            const dateStr = (event as MatDatepickerInputEvent<Moment>).value.format('YYYY-MM-DD');
            this.selectedItems.push({ label: dateStr, value: dateStr } as ChooserBarItem);
        } else {
            const foundItem = this.findItem((event as MatAutocompleteSelectedEvent).option.viewValue);
            if (foundItem) {
                this.selectedItems.push(foundItem);
            }
        }

        this.itemInput.nativeElement.value = '';
        this.itemCtrl.setValue(null);
        this.onChange(this.value);
        this.stateChanges.next();
    }

    private filterByValue(value: string): ChooserBarItem[] {
        const filterValue = value.toLowerCase();
        return this.items
            .filter((item) => {
                return item.label.toLowerCase().indexOf(filterValue) >= 0;
            })
            .filter((item) => {
                return !this.selectedItems.map((si) => si.value).includes(item.value);
            });
    }

    private filterByUnselected(): ChooserBarItem[] {
        return this.items
            .filter((item) => {
                return !this.selectedItems.map((si) => si.value).includes(item.value);
            });
    }

    drop(event: CdkDragDrop<ChooserBarItem[]>) {
        moveItemInArray(this.selectedItems, event.previousIndex, event.currentIndex);
        this.onChange(this.value);
        this.stateChanges.next();
    }
}
